import { useDmsState } from '@packages/contexts/dms';
import React, { FC, useCallback, useMemo } from 'react';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { StyleSheet } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { BmisBatteryBadge } from '@web/bmis/components/bmis-battery-badge';
import { getDisplayDate, getTimeAgoString } from '@web/utils';
import { DATE_FORMAT } from '@web/config/constants';
import { usePrint } from '@web/components/printable-container';
import { DMSPdf } from './dms-pdf';

const styles = StyleSheet.create({
    typographyStyles: {
        fontSize: 21,
        fontWeight: '600',
        lineHeight: 32,
    },
    button: {
        height: 40,
        width: 40,
    },
});

export const DmsHeader: FC = () => {
    const navigate = useNavigate();
    const { selectedRepairOrder, hasHistoryFetched } = useDmsState();
    const { vin: vinString } = useParams<{ vin: string }>();

    const handleButtonPress = useCallback(() => {
        if (window.history.state && window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(`/vin/${vinString}`);
        }
    }, [navigate, vinString]);

    const printableDocumentName = useMemo(() => {
        return `DMS_Report_${vinString}_${getDisplayDate(new Date(), DATE_FORMAT.YYYY_MM_DD)}`;
    }, [vinString]);

    const { componentRef, print, isPrintLoading } = usePrint({ documentTitle: printableDocumentName });

    const headerBadgeText = React.useMemo(() => {
        if (
            !hasHistoryFetched ||
            !selectedRepairOrder ||
            (selectedRepairOrder && Object.keys(selectedRepairOrder).length === 0)
        )
            return '';
        return getTimeAgoString(selectedRepairOrder?.closedDate ?? '');
    }, [selectedRepairOrder, hasHistoryFetched]);

    return (
        <>
            <div className="d-flex align-items-center">
                <div className="mr-4">
                    <Button variant="ghost-blue" style={styles.button} onPress={handleButtonPress}>
                        <Icon name="arrow-back" color="brandSecondary" size={24} />
                    </Button>
                </div>
                <div className="mb-0 mr-4">
                    <Typography variant="h3" style={styles.typographyStyles}>
                        #{selectedRepairOrder?.roNumber}
                    </Typography>
                </div>
                <BmisBatteryBadge label={headerBadgeText} />
            </div>
            <>
                <div className="d-flex align-items-center">
                    <div className="ml-6">
                        <Button variant="ghost-blue" disabled={isPrintLoading} style={styles.button} onPress={print}>
                            <Icon name="sync-down" color="blueOne" size={24} />
                        </Button>
                    </div>
                </div>
                <DMSPdf componentRef={componentRef} isPrintLoading={isPrintLoading} />
            </>
        </>
    );
};
